import { useEffect, useMemo, useRef, useState } from 'react';
import { LiveUserLocation, UserAccount } from '../types';
import { attemptSessionRenewal, getAuthToken } from './auth';

interface Options {
  farmId?:string|null;
  enabled: boolean; broadcastEnabled: boolean; idToken: string | null; user: UserAccount | null;
  sessionId: string; currentLocation: GeolocationPosition | null; currentHeadingDeg?: number | null;
}
export const useLiveGpsChannel = (options: Options) => {
  const { enabled, idToken, user, sessionId, farmId } = options;
  const [status, setStatus] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [peerMap, setPeerMap] = useState<Record<string, LiveUserLocation>>({});
  const [config, setConfig] = useState({ lastKnownTtlMs: 240 * 60000, liveFreshMs: 45000 });
  const [now, setNow] = useState(Date.now());
  const latest = useRef(options); latest.current = options;
  const wsRef = useRef<WebSocket | null>(null);
  const lastSent = useRef(0);
  const lastReceived = useRef(0);
  const stoppedSocket = useRef<WebSocket | null>(null);
  const publish = (force = false) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const { broadcastEnabled, currentLocation: position, currentHeadingDeg } = latest.current;
    if (!broadcastEnabled) {
      if (stoppedSocket.current !== ws) ws.send(JSON.stringify({ type: 'location:stop' }));
      stoppedSocket.current = ws; return;
    }
    stoppedSocket.current = null;
    if (!position) return;
    if (!force && Date.now() - lastSent.current < 1000) return;
    lastSent.current = Date.now();
    ws.send(JSON.stringify({ type: 'location:update', payload: {
      lat: position.coords.latitude, lng: position.coords.longitude, accuracyM: position.coords.accuracy,
      headingDeg: currentHeadingDeg ?? position.coords.heading, speedMps: position.coords.speed, ts: position.timestamp,
    } }));
  };
  useEffect(() => {
    setPeerMap({});
    if (!enabled || !idToken || !user?.email || typeof WebSocket === 'undefined') { setStatus('idle'); return; }
    let disposed = false, retries = 0, timer: ReturnType<typeof setTimeout> | undefined;
    let token = idToken;
    const connect = () => {
      if (disposed || document.visibilityState === 'hidden') return;
      clearTimeout(timer);
      if (wsRef.current && wsRef.current.readyState <= WebSocket.OPEN) return;
      setStatus('connecting');
      const base = (import.meta as any).env?.VITE_API_URL || window.location.origin;
      const url = new URL('/live/gps', base.replace(/^http/, 'ws'));
      url.searchParams.set('token', token); url.searchParams.set('sessionId', sessionId);
      url.searchParams.set('farmId',farmId||'');
      const ws = new WebSocket(url); wsRef.current = ws;
      ws.onopen = () => {
        if (disposed || wsRef.current !== ws) return;
        retries = 0; setStatus('connected'); setError(null);
        // Publish immediately: a GPS result may have arrived before the socket opened.
        lastReceived.current = Date.now(); publish(true);
      };
      ws.onmessage = event => {
        if (disposed || wsRef.current !== ws) return;
        lastReceived.current = Date.now();
        try {
          const message = JSON.parse(event.data);
          const normalize = (location: LiveUserLocation) => location?.userSub &&
            Number.isFinite(location.coords?.lat) && Number.isFinite(location.coords?.lng) &&
            Number.isFinite(location.coords?.ts) && Math.abs(location.coords.lat) <= 90 && Math.abs(location.coords.lng) <= 180;
          if (message.type === 'locations:bootstrap') {
            if (Number.isFinite(message.config?.lastKnownTtlMs) && message.config.lastKnownTtlMs > 0 &&
                Number.isFinite(message.config?.liveFreshMs) && message.config.liveFreshMs > 0) setConfig(message.config);
            const next: Record<string, LiveUserLocation> = {};
            if (Array.isArray(message.payload)) message.payload.forEach((location: LiveUserLocation) => {
              if (normalize(location)) next[location.userSub] = location;
            });
            setPeerMap(next);
          } else if (message.type==='locations:config' && Number.isFinite(message.config?.lastKnownTtlMs)&&message.config.lastKnownTtlMs>0){setConfig(message.config);
          } else if (message.type === 'location:update' && normalize(message.payload)) {
            setPeerMap(previous => ({ ...previous, [message.payload.userSub]: message.payload }));
          } else if (message.type === 'location:remove') {
            setPeerMap(previous => { const next = { ...previous }; delete next[message.payload.userSub]; return next; });
          }
        } catch { /* Ignore malformed frames; reconnect keeps the last usable positions. */ }
      };
      ws.onerror = () => { if (!disposed) setError('Live connection interrupted. Retrying automatically.'); };
      ws.onclose = async event => {
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        if (disposed) return;
        setStatus('connecting');
        if (event.code === 4403) { setPeerMap({});setStatus('error'); setError('Live locations are unavailable for your account.'); return; }
        if (event.code === 4401) {
          if (await attemptSessionRenewal()) token = getAuthToken() || token;
          else { setStatus('error'); setError('Sign in again to reconnect live locations.'); return; }
        }
        if (disposed) return;
        timer = setTimeout(connect, Math.min(30000, 1000 * 2 ** Math.min(retries++, 5)));
      };
    };
    const suspend = () => { clearTimeout(timer); wsRef.current?.close(); };
    const resume = () => { if (document.visibilityState === 'hidden') suspend(); else connect(); };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('online', connect); window.addEventListener('pageshow', connect); window.addEventListener('pagehide', suspend);
    const heartbeat = setInterval(() => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        if (Date.now() - lastReceived.current > 60000) ws.close();
        else ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
    connect();
    return () => {
      disposed = true; clearTimeout(timer); clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('online', connect); window.removeEventListener('pageshow', connect); window.removeEventListener('pagehide', suspend);
      // Turning sharing off must remove the current position before demand can
      // close the socket. Unmounting a map alone retains last-seen semantics.
      if (!latest.current.broadcastEnabled && wsRef.current?.readyState === WebSocket.OPEN && stoppedSocket.current !== wsRef.current) {
        wsRef.current.send(JSON.stringify({type:'location:stop'}));
      }
      wsRef.current?.close(); wsRef.current = null;
    };
  }, [enabled, idToken, user?.email, sessionId,farmId]);
  useEffect(() => { publish(); }, [options.currentLocation, options.currentHeadingDeg, options.broadcastEnabled]);
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 5000); return () => clearInterval(timer); }, []);
  const peerLocations = useMemo(() => Object.values(peerMap)
    .filter(location => location.userEmail !== user?.email && now - location.coords.ts < config.lastKnownTtlMs)
    .map(location => ({ ...location, isLive: !!location.isLive && status === 'connected' && now - location.coords.ts < config.liveFreshMs }))
    .sort((a, b) => b.coords.ts - a.coords.ts), [peerMap, user?.email, now, config, status]);
  return { peerLocations, status, error, ...config };
};
